import { useEffect, useRef, useState } from 'react';

/**
 * Animates a numeric value from 0 up to `end` on mount (and whenever `end`
 * changes), using premium easeOutExpo timing. Purely presentational — it
 * never alters the underlying value, only the number shown during the
 * brief mount animation.
 */
export const useCountUp = (
  end: number,
  duration = 1200,
): number => {
  const [value, setValue] = useState(0);
  const frameRef = useRef<number | null>(null);
  const startRef = useRef<number | null>(null);

  useEffect(() => {
    const target = Number.isFinite(end) ? end : 0;

    // Respect reduced-motion: jump straight to the final value.
    const prefersReduced =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    if (prefersReduced || target === 0) {
      setValue(target);
      return;
    }

    startRef.current = null;

    const easeOutExpo = (t: number) =>
      t === 1 ? 1 : 1 - Math.pow(2, -10 * t);

    const tick = (now: number) => {
      if (startRef.current === null) startRef.current = now;
      const elapsed = now - startRef.current;
      const progress = Math.min(elapsed / duration, 1);
      setValue(target * easeOutExpo(progress));
      if (progress < 1) {
        frameRef.current = requestAnimationFrame(tick);
      } else {
        setValue(target);
      }
    };

    frameRef.current = requestAnimationFrame(tick);

    return () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    };
  }, [end, duration]);

  return value;
};

export default useCountUp;
